// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/users/AdminUserDetailPage.tsx
//
// Phase 8.2 Wave 7 Task 50 — Admin User Detail page.
//
// Route: /users/:id
//
// Layout (top → bottom):
//   1. Sticky header (name + status + primary role badge + email)
//   2. 4 sections (Profile / Permissions / Activity / Sessions) rendered
//      as a Tabs-equivalent (since @printway/ui ships no Accordion/Tabs
//      primitive yet, we use a lightweight pill button group with
//      conditional render per section).
//
// vg-binding: PLAN:task-50
// vg-binding: P8.D-101  — permissions rendered as resolved tree
// vg-binding: P8.D-380  — mutations produce audit entries (Activity reflects)
// vg-binding: P8.D-475  — self-action guard: hide revoke + status-disable
//                          buttons when viewing own account; show banner
// vg-binding: G-45      — covers test goal G-45

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, useAuth, cn } from '@printway/ui';

import {
  getAdminUserDetail,
  type AdminUserApiError,
  type AdminUserDetail,
} from '../../api/admin-users.api.js';
import { BackLink } from '../../components/ui/BackLink.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { AdminUserProfileSection } from '../../features/users/AdminUserProfileSection.js';
import { AdminUserPermissionsSection } from '../../features/users/AdminUserPermissionsSection.js';
import { AdminUserCustomRolesSection } from '../../features/users/AdminUserCustomRolesSection.js';
import { AdminUserActivitySection } from '../../features/users/AdminUserActivitySection.js';
import { AdminUserSessionsSection } from '../../features/users/AdminUserSessionsSection.js';

type TabKey = 'profile' | 'permissions' | 'activity' | 'sessions';

// Tab identity + test ids are code-level; the human-facing label is resolved
// through i18n (`detail.tabs.<key>`) at render time.
const TABS: { key: TabKey; testId: string }[] = [
  { key: 'profile', testId: 'admin-user-tab-profile' },
  { key: 'permissions', testId: 'admin-user-tab-permissions' },
  { key: 'activity', testId: 'admin-user-tab-activity' },
  { key: 'sessions', testId: 'admin-user-tab-sessions' },
];

// Statuses with a localized label under `detail.statusLabels.*`; anything
// else falls back to the raw enum value.
const DETAIL_STATUSES: string[] = ['active', 'inactive', 'locked'];

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300',
  inactive: 'bg-pw-surface text-pw-text',
  locked: 'bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300',
};

export function AdminUserDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const { t } = useTranslation('users');
  const [tab, setTab] = useState<TabKey>('profile');

  // Sync tab with URL search params so deep-links work.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const t = sp.get('tab');
    if (t && TABS.some((x) => x.key === t)) {
      setTab(t as TabKey);
    }
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (tab === 'profile') url.searchParams.delete('tab');
    else url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url.toString());
  }, [tab]);

  const detailQuery = useQuery<AdminUserDetail, AdminUserApiError | Error>({
    queryKey: ['admin', 'users', id],
    queryFn: async () => getAdminUserDetail(id),
    enabled: id.length > 0,
  });

  if (!id) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 p-4 text-sm text-rose-800 dark:text-rose-300">
          {t('detail.missingId')}
        </div>
      </div>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <div className="h-24 animate-pulse rounded-md bg-pw-surface" />
        <div className="h-10 animate-pulse rounded-md bg-pw-surface" />
        <div className="h-64 animate-pulse rounded-md bg-pw-surface" />
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    const err = detailQuery.error as AdminUserApiError;
    return (
      <div className="space-y-3 p-6">
        <BackLink
          to="/users"
          label={t('detail.backToList')}
          testId="admin-user-detail-error-back"
        />
        <div className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 p-4 text-sm text-rose-800 dark:text-rose-300">
          {t('detail.loadError', {
            message:
              err?.userMessage ??
              (detailQuery.error as Error)?.message ??
              t('detail.unknownError'),
          })}
        </div>
      </div>
    );
  }

  const detail = detailQuery.data;
  const isSelf = currentUser?.id === detail.id;

  return (
    <div className="space-y-4 p-6">
      {/* Back link */}
      <BackLink
        to="/users"
        label={t('detail.back')}
        testId="admin-user-detail-back"
      />

      {/* Sticky header — name + role + status + email */}
      <PageHeader
        className="sticky top-0 z-10 -mx-6 border-b border-pw-border bg-pw-bg px-6 pb-4 pt-2"
        title={detail.name || detail.email}
        titleAddon={
          <>
            <span
              className={cn(
                'rounded-full px-2.5 py-0.5 text-xs font-medium',
                STATUS_BADGE[detail.status] ?? 'bg-pw-surface text-pw-text',
              )}
            >
              {DETAIL_STATUSES.includes(detail.status)
                ? t(`detail.statusLabels.${detail.status}`)
                : detail.status}
            </span>
            <span className="rounded bg-pw-primary-strong/10 px-2 py-0.5 text-xs font-medium text-pw-primary-strong">
              {detail.primary_role}
            </span>
            {detail.additional_roles.length > 0 && (
              <span className="text-xs text-pw-text-muted">
                {t('detail.additionalRoles', {
                  count: detail.additional_roles.length,
                })}
              </span>
            )}
          </>
        }
        subtitle={detail.email}
      />

      {/* Self-action banner */}
      {isSelf && (
        <div
          className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-900"
          data-test-id="admin-user-self-banner"
        >
          {t('detail.selfBanner')}
        </div>
      )}

      {/* Tab nav */}
      <nav className="flex flex-wrap gap-2 border-b border-pw-border">
        {TABS.map((tabItem) => (
          <button
            key={tabItem.key}
            type="button"
            data-test-id={tabItem.testId}
            onClick={() => setTab(tabItem.key)}
            className={cn(
              'px-3 py-2 text-sm font-medium transition-colors',
              tab === tabItem.key
                ? 'border-b-2 border-pw-primary-strong text-pw-primary-strong'
                : 'text-pw-text-muted hover:text-pw-text',
            )}
            aria-selected={tab === tabItem.key}
            role="tab"
          >
            {t(`detail.tabs.${tabItem.key}`)}
          </button>
        ))}
      </nav>

      {/* Tab panel */}
      <section className="rounded-lg border border-pw-border bg-pw-bg p-4">
        {tab === 'profile' && (
          <AdminUserProfileSection user={detail} isSelf={isSelf} />
        )}
        {tab === 'permissions' && (
          <div className="flex flex-col gap-4">
            <AdminUserCustomRolesSection
              userId={detail.id}
              assignedCodes={detail.additional_roles}
              onChanged={() => void detailQuery.refetch()}
            />
            <AdminUserPermissionsSection
              effectivePermissions={detail.effective_permissions}
              customPermissions={detail.custom_permissions}
            />
          </div>
        )}
        {tab === 'activity' && <AdminUserActivitySection userId={detail.id} />}
        {tab === 'sessions' && (
          <AdminUserSessionsSection
            userId={detail.id}
            isSelf={isSelf}
            lastLoginAt={detail.last_login_at}
            lastLoginIp={detail.last_login_ip}
          />
        )}
      </section>
    </div>
  );
}
